import React, { useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Sparkles, Copy, Check } from "lucide-react";
import { buildTripRecap } from "@/utils/segmentDuration";

/**
 * Tweet-worthy one-line recap that appears at the top of the Trip Details
 * page once the trip has fully passed. Always recomputed from current
 * segments — if the user edits a segment after trip-end, the recap adjusts
 * automatically on next render.
 *
 * Returns null (no card) while the trip is ongoing or when there's nothing
 * recap-worthy to show.
 */
const TripRecapBanner = ({ trip, segments }) => {
  const [copied, setCopied] = useState(false);
  // Filter cancelled / mistake segments BEFORE handing off to the recap
  // builder — matches the pattern used everywhere else (e.g.
  // `calculatePlannedNights`, the "67n" header counter). Without this,
  // cancelled accommodations stretch the trip-start/end window and
  // inflate the day count, plus they get counted as "beds" / extra
  // flights. The builder itself trusts its input (per its own
  // docstring), so the filter belongs here in the caller.
  const activeSegments = Array.isArray(segments)
    ? segments.filter((s) => !s?.is_cancelled && !s?.is_mistake)
    : [];
  const recap = buildTripRecap(activeSegments, trip);
  if (!recap) return null;

  const shareText = `🧳 ${trip?.name ? trip.name + " — " : ""}${recap.text}`;

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(shareText);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Clipboard unavailable (http/iframe) — silently no-op. Text is still
      // selectable by the user if they want to copy it manually.
    }
  };

  return (
    <Card
      data-testid="trip-recap-banner"
      className="border-2 border-violet-200 dark:border-violet-800 bg-gradient-to-r from-violet-50 via-fuchsia-50 to-pink-50 dark:from-violet-950/40 dark:via-fuchsia-950/40 dark:to-pink-950/40 shadow-sm"
    >
      <CardContent className="py-3 px-4 flex items-start gap-3">
        <Sparkles className="h-5 w-5 text-violet-600 dark:text-violet-300 flex-shrink-0 mt-0.5" />
        <div className="flex-1 min-w-0">
          <div className="text-[11px] uppercase tracking-wider font-medium text-violet-700 dark:text-violet-300">
            Trip recap
          </div>
          <div
            className="text-sm sm:text-base text-gray-800 dark:text-gray-100 break-words whitespace-normal leading-snug"
            data-testid="trip-recap-text"
          >
            {recap.text}
          </div>
        </div>
        <Button
          data-testid="trip-recap-copy-btn"
          variant="ghost"
          size="sm"
          onClick={handleCopy}
          className="flex-shrink-0 h-8 px-2 text-violet-700 dark:text-violet-300 hover:bg-violet-100 dark:hover:bg-violet-900/40"
          title={copied ? "Copied!" : "Copy recap"}
        >
          {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
          <span className="ml-1 hidden sm:inline text-xs">{copied ? "Copied" : "Copy"}</span>
        </Button>
      </CardContent>
    </Card>
  );
};

export default TripRecapBanner;
